import * as z from 'zod';
import { PRODUCT_MODERATION_FILTERS } from '@/entities/menu';

/**
 * URL filter shared by the two menu routes (`/eats/menu` and
 * `/eats/menu/:categoryId`) — they render the same screen, so they validate
 * the same search state.
 */
export const MENU_SECTIONS = [
  'branches',
  'categories',
  'products',
  'promotions',
] as const;
export type MenuSection = (typeof MENU_SECTIONS)[number];

/**
 * Where each Master Menu section lives. Every section is its own page, so a
 * legacy `/eats/menu?section=<name>` link redirects here rather than
 * rendering in place.
 */
export const MENU_SECTION_PATHS: Record<MenuSection, string> = {
  branches: '/eats/menu/branches',
  categories: '/eats/menu/categories',
  products: '/eats/menu/products',
  promotions: '/eats/menu/promotions',
};

/** Resolve a `?section=` value to its page — unknown values fall back to branches. */
export function menuSectionPath(section: MenuSection | undefined): string {
  if (section !== undefined && section in MENU_SECTION_PATHS) {
    return MENU_SECTION_PATHS[section];
  }
  return MENU_SECTION_PATHS.branches;
}

/**
 * URL filter shared by the Master Menu routes (`/eats/menu` and
 * sub-sections) — validates the section and contextual search state.
 */
export const menuSearchSchema = z.object({
  section: z.enum(MENU_SECTIONS).optional().catch(undefined),
  search: z.string().optional().catch(''),
  /**
   * The active moderation pill. Absent is «Все» — the design's default — so
   * there is no `'all'` member: a param that means "no filter" is one the URL
   * should simply not carry.
   */
  status: z.string().optional().catch(undefined),
  /**
   * The category status filter — a separate axis from product status.
   */
  categoryStatus: z.enum(PRODUCT_MODERATION_FILTERS).optional().catch(undefined),
  /** Selected category ID for the Products section filter. */
  categoryId: z.string().optional().catch(undefined),
  /** Selected branch ID for Products / Promotions filters. */
  branchId: z.string().optional().catch(undefined),
  /**
   * True while the toolbar's «Все филиалы» entry is picked.
   */
  allBranches: z.boolean().optional().catch(undefined),
  /** Start date (YYYY-MM-DD) for promotions date range filter. */
  from: z.string().optional().catch(undefined),
  /** End date (YYYY-MM-DD) for promotions date range filter. */
  to: z.string().optional().catch(undefined),
  /** When true, showcases the published customer-facing view in read-only mode. */
  showcase: z.boolean().optional().catch(undefined),
});

export type MenuSearch = z.infer<typeof menuSearchSchema>;
